import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {ScrollRestoration | ''} */
  #prevScrollRestoration = '';

  /** MS: true only while THIS page pushed the {cartDrawerOpen} entry, so close never calls history.back() for a stale entry */
  #pushedHistory = false;

  /** MS: a page restored from bfcache / back-forward can sit on a stale {cartDrawerOpen} entry with the drawer closed */
  #clearStaleHistory = () => {
    if (history.state?.cartDrawerOpen && !this.refs.dialog?.open) {
      this.#pushedHistory = false;
      history.replaceState(null, '');
    }
  };

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
    window.addEventListener('pageshow', this.#clearStaleHistory);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
    window.removeEventListener('pageshow', this.#clearStaleHistory);
  }

  /** MS: Instagram/FB/etc in-app browsers treat history.back() as leaving the shop (blank/dim overlay). */
  #isInAppBrowser() {
    return /Instagram|FBAN|FBAV|Facebook|Line\/|TikTok|BytedanceWebview|Twitter|LinkedInApp/i.test(
      navigator.userAgent || ''
    );
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;
    // MS: skip history hijack in in-app browsers — their back stack exits the webview
    if (this.#isInAppBrowser()) return;

    // MS: a leftover {cartDrawerOpen} entry we didn't push is stale; replace it so close can't go back past this page
    if (history.state?.cartDrawerOpen && !this.#pushedHistory) history.replaceState(null, '');
    if (!history.state?.cartDrawerOpen) {
      // MS: scroll restoration is per history entry. Mark the page's own entry 'manual' before
      // pushing (its scroll is recorded as 0 while the body is scroll-locked), so going back
      // on close doesn't scroll the page to the top.
      this.#prevScrollRestoration = history.scrollRestoration;
      history.scrollRestoration = 'manual';
      history.pushState({ cartDrawerOpen: true }, '');
      this.#pushedHistory = true;
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    const pushed = this.#pushedHistory;
    this.#pushedHistory = false;
    if (pushed && history.state?.cartDrawerOpen) {
      // MS: clear the flag in place — never history.back() on X close.
      // history.back() jumps scroll and, in Instagram/Facebook in-app browsers, leaves the
      // shop on a blank/dim overlay. Android back-to-close still uses the pushed entry + popstate.
      history.replaceState(null, '');
      const prev = this.#prevScrollRestoration || 'auto';
      history.scrollRestoration = prev;
      this.#prevScrollRestoration = '';
    }
  };

  #handlePopState = async () => {
    this.#pushedHistory = false;
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
